"use client";

import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { BookOpen, LineChart, RotateCcw, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CalculatorTemplate } from "@/components/calculator/CalculatorTemplate";
import { InputForm } from "@/components/calculator/InputForm";
import { ResultCard } from "@/components/calculator/ResultCard";
import { AreaChartWrapper, PieChartWrapper } from "@/components/calculator/ChartWrapperLazy";
import { ExplanationTable } from "@/components/calculator/ExplanationTable";
import { ShareButtons } from "@/components/calculator/ShareButtons";
import { formatCurrency } from "@/lib/utils/currency";
import { calculateSavingsGoal } from "@/lib/calculators/savings-goal";

const MAX_AMOUNT = 999_999_999_999;

const schema = z.object({
  targetAmount: z.coerce
    .number()
    .min(1, "Enter a positive amount")
    .max(MAX_AMOUNT, "Maximum is $999 trillion"),
  currentSavings: z.coerce.number().min(0, "Must be 0 or more").max(MAX_AMOUNT),
  monthlyContribution: z.coerce
    .number()
    .min(0, "Must be 0 or more")
    .max(MAX_AMOUNT),
  expectedReturn: z.coerce.number().min(0, "Enter a valid rate").max(100),
  years: z.coerce.number().min(1, "Enter at least 1 year").max(50),
  solveMode: z.enum(["time", "monthly"]),
  inflationAdjusted: z.union([z.boolean(), z.enum(["true", "false"])]),
  inflationRate: z.coerce.number().min(0).max(20),
  compoundingFrequency: z.enum(["monthly", "quarterly", "annual"]),
});

type FormValues = z.infer<typeof schema>;

const defaultValues: FormValues = {
  targetAmount: 50000,
  currentSavings: 10000,
  monthlyContribution: 500,
  expectedReturn: 6,
  years: 5,
  solveMode: "time",
  inflationAdjusted: false,
  inflationRate: 3,
  compoundingFrequency: "monthly",
};

const PRESETS: { label: string; values: Partial<FormValues> }[] = [
  {
    label: "Emergency fund",
    values: {
      targetAmount: 15000,
      currentSavings: 3000,
      monthlyContribution: 500,
      expectedReturn: 4,
      years: 2,
    },
  },
  {
    label: "Down payment",
    values: {
      targetAmount: 60000,
      currentSavings: 10000,
      monthlyContribution: 1000,
      expectedReturn: 5,
      years: 4,
    },
  },
  {
    label: "Vacation fund",
    values: {
      targetAmount: 5000,
      currentSavings: 0,
      monthlyContribution: 200,
      expectedReturn: 2,
      years: 2,
    },
  },
];

const CHART_COLOR = "#2563eb";
const PIE_COLORS = {
  savings: "#64748b",
  contributions: "#0ea5e9",
  growth: "#2563eb",
};

function parseValuesFromSearchParams(
  params: URLSearchParams
): Partial<FormValues> | null {
  const target = params.get("target");
  const current = params.get("current");
  const monthly = params.get("monthly");
  const rate = params.get("rate");
  const years = params.get("years");
  const mode = params.get("mode");
  const inflation = params.get("inflation");
  const infRate = params.get("infRate");
  if (!target || !current || !rate) return null;
  return {
    targetAmount: parseFloat(target),
    currentSavings: parseFloat(current),
    monthlyContribution: monthly ? parseFloat(monthly) : 500,
    expectedReturn: parseFloat(rate),
    years: years ? parseInt(years, 10) : 5,
    solveMode: mode === "monthly" ? "monthly" : "time",
    inflationAdjusted: inflation === "1" || inflation === "true",
    inflationRate: infRate ? parseFloat(infRate) : 3,
    compoundingFrequency:
      (["monthly", "quarterly", "annual"] as const).includes(
        (params.get("compound") ?? "") as "monthly" | "quarterly" | "annual"
      )
        ? ((params.get("compound") ?? "monthly") as "monthly" | "quarterly" | "annual")
        : "monthly",
  };
}

function buildShareUrl(values: FormValues): string {
  const params = new URLSearchParams();
  params.set("target", String(values.targetAmount));
  params.set("current", String(values.currentSavings));
  params.set("monthly", String(values.monthlyContribution));
  params.set("rate", String(values.expectedReturn));
  params.set("years", String(values.years));
  params.set("mode", values.solveMode);
  params.set("inflation", values.inflationAdjusted ? "1" : "0");
  params.set("infRate", String(values.inflationRate));
  params.set("compound", values.compoundingFrequency);
  if (typeof window !== "undefined") {
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  }
  return "";
}

function useMounted() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}

export function SavingsGoalCalculator() {
  const mounted = useMounted();
  const [formValues, setFormValues] = useState<FormValues>(defaultValues);
  const [resetKey, setResetKey] = useState(0);
  const [result, setResult] = useState(() =>
    calculateSavingsGoal({
      ...defaultValues,
      inflationAdjusted: false,
      inflationRate: defaultValues.inflationRate,
    })
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const parsed = parseValuesFromSearchParams(params);
    if (parsed) {
      const merged = { ...defaultValues, ...parsed } as FormValues;
      const parsedResult = schema.safeParse(merged);
      if (parsedResult.success) {
        const data = parsedResult.data;
        setFormValues(data);
        setResult(calculateSavingsGoal({
          ...data,
          inflationAdjusted: data.inflationAdjusted === "true" || data.inflationAdjusted === true,
        }));
        setResetKey((k) => k + 1);
      }
    }
  }, []);

  const handleReset = useCallback(() => {
    setFormValues(defaultValues);
    setResult(calculateSavingsGoal({
      ...defaultValues,
      inflationAdjusted: false,
      inflationRate: defaultValues.inflationRate,
    }));
    setResetKey((k) => k + 1);
  }, []);

  const toCalculatorInput = useCallback((v: FormValues) => ({
    ...v,
    inflationAdjusted: v.inflationAdjusted === "true" || v.inflationAdjusted === true,
  }), []);

  const handlePreset = useCallback((preset: Partial<FormValues>) => {
    const merged = { ...defaultValues, ...formValues, ...preset } as FormValues;
    const parsed = schema.safeParse(merged);
    if (parsed.success) {
      const data = parsed.data;
      setFormValues(data);
      setResult(calculateSavingsGoal(toCalculatorInput(data)));
      setResetKey((k) => k + 1);
    }
  }, [formValues, toCalculatorInput]);

  const handleChange = useCallback((values: FormValues) => {
    setFormValues(values);
    setResult(calculateSavingsGoal(toCalculatorInput(values)));
  }, [toCalculatorInput]);

  const baseFields = [
    {
      name: "solveMode" as const,
      label: "Solve for",
      tooltip: "Choose what you want to calculate: time to goal or required monthly savings.",
      inputType: "select" as const,
      options: [
        { value: "time", label: "Time to goal (I know my monthly savings)" },
        { value: "monthly", label: "Required monthly (I have a deadline)" },
      ],
    },
    {
      name: "inflationAdjusted" as const,
      label: "Adjust for inflation",
      tooltip: "Target in today's dollars. We'll calculate the nominal amount you need in the future.",
      inputType: "select" as const,
      options: [
        { value: "false", label: "No" },
        { value: "true", label: "Yes" },
      ],
    },
    ...((formValues.inflationAdjusted === "true" || formValues.inflationAdjusted === true)
      ? [
          {
            name: "inflationRate" as const,
            label: "Expected inflation",
            tooltip: "Annual inflation rate (e.g., 3 for 3%). Historical average is ~3%.",
            suffix: "%",
            min: 0,
            max: 20,
            step: 0.5,
            inputMode: "decimal" as const,
          },
        ]
      : []),
    {
      name: "targetAmount" as const,
      label: "Target amount",
      tooltip: (formValues.inflationAdjusted === "true" || formValues.inflationAdjusted === true)
        ? "Amount in today's purchasing power."
        : "The amount you want to save.",
      suffix: " USD",
      min: 1,
      max: MAX_AMOUNT,
      step: 1,
      inputMode: "decimal" as const,
    },
    {
      name: "currentSavings" as const,
      label: "Current savings",
      tooltip: "What you have saved so far.",
      suffix: " USD",
      min: 0,
      max: MAX_AMOUNT,
      step: 1,
      inputMode: "decimal" as const,
    },
    {
      name: "compoundingFrequency" as const,
      label: "Compounding",
      tooltip: "How often interest compounds. Most savings accounts and investments compound monthly.",
      inputType: "select" as const,
      options: [
        { value: "monthly", label: "Monthly" },
        { value: "quarterly", label: "Quarterly" },
        { value: "annual", label: "Annual" },
      ],
    },
    {
      name: "expectedReturn" as const,
      label: "Expected return",
      tooltip: "Annual investment return (e.g., 6 for 6%).",
      suffix: "%",
      min: 0,
      max: 100,
      step: 0.5,
      inputType: "slider-number" as const,
      inputMode: "decimal" as const,
    },
  ];

  const timeModeFields = [
    ...baseFields,
    {
      name: "monthlyContribution" as const,
      label: "Monthly contribution",
      tooltip: "Amount you can save each month. We'll calculate how long until you reach your goal.",
      suffix: " USD",
      min: 0,
      max: MAX_AMOUNT,
      step: 1,
      inputMode: "decimal" as const,
    },
  ];

  const monthlyModeFields = [
    ...baseFields,
    {
      name: "years" as const,
      label: "Time horizon",
      tooltip: "Years until you need the money. We'll calculate the required monthly savings.",
      suffix: " years",
      min: 1,
      max: 50,
      sliderMax: 30,
      step: 1,
      inputType: "slider-number" as const,
      inputMode: "decimal" as const,
    },
  ];

  const fields =
    formValues.solveMode === "time" ? timeModeFields : monthlyModeFields;

  const chartData = result.chartData.map((d) => ({
    label: `Year ${d.year}`,
    balance: d.balance,
  }));

  const formatYearsMonths = (years: number, months: number | null) => {
    if (months != null && months < 12) return `${months} months`;
    const totalMonths = months ?? Math.round(years * 12);
    const y = Math.floor(totalMonths / 12);
    const m = totalMonths % 12;
    if (y === 0) return `${m} months`;
    if (m > 0) return `${y} years ${m} months`;
    return `${y} years`;
  };

  const metrics = [
    ...(formValues.solveMode === "time" && result.yearsToGoal != null
      ? [
          {
            label: "Time to goal",
            value: formatYearsMonths(
              result.yearsToGoal,
              result.monthsToGoal
            ),
            highlight: true,
            tooltip: "How long until you reach your goal with your current monthly savings.",
          },
        ]
      : formValues.solveMode === "monthly" &&
          result.requiredMonthlyContribution != null
        ? [
            {
              label: "Required monthly",
              value: formatCurrency(result.requiredMonthlyContribution),
              highlight: true,
              tooltip: "How much you need to save each month to reach your goal on time.",
            },
          ]
        : []),
    {
      label: "Projected balance",
      value: formatCurrency(result.projectedBalance),
      highlight: formValues.solveMode === "time" ? false : true,
    },
    {
      label: "Goal status",
      value: result.goalReached ? "Goal reached" : "Short of goal",
    },
    ...((formValues.inflationAdjusted === "true" || formValues.inflationAdjusted === true) && result.nominalTarget !== formValues.targetAmount
      ? [
          {
            label: "Target in future $",
            value: formatCurrency(result.nominalTarget),
            tooltip: "Amount you'll need to have the purchasing power of your target after inflation.",
          },
        ]
      : []),
    {
      label: "Total contributions",
      value: formatCurrency(result.totalContributions),
    },
    {
      label: "Investment growth",
      value: formatCurrency(result.totalGrowth),
    },
  ];

  return (
    <CalculatorTemplate
      chartSectionLabel="Progress over time"
      chartSectionIcon={LineChart}
      explanationSectionLabel="Formula & examples"
      explanationSectionIcon={BookOpen}
      inputsSection={
        <div className="space-y-4">
          <InputForm
            key={resetKey}
            schema={schema}
            defaultValues={formValues}
            fields={fields}
            onChange={handleChange}
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Presets:</span>
            {PRESETS.map((p) => {
              const isSelected = Object.entries(p.values).every(
                ([k, v]) => formValues[k as keyof FormValues] === v
              );
              return (
                <Button
                  key={p.label}
                  type="button"
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePreset(p.values)}
                  className="text-xs"
                >
                  {p.label}
                </Button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <ShareButtons shareUrl={buildShareUrl(formValues)} />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="text-muted-foreground"
            >
              <RotateCcw className="size-3.5" />
              Reset
            </Button>
          </div>
        </div>
      }
      resultsSection={
        <div className="space-y-4">
          <ResultCard icon={Target} metrics={metrics} />
          {mounted &&
            result.projectedBalance > 0 &&
            (() => {
              const pieData = [
                {
                  name: "Current savings",
                  value: formValues.currentSavings,
                  color: PIE_COLORS.savings,
                },
                {
                  name: "Contributions",
                  value: result.totalContributions,
                  color: PIE_COLORS.contributions,
                },
                {
                  name: "Growth",
                  value: result.totalGrowth,
                  color: PIE_COLORS.growth,
                },
              ].filter((d) => d.value > 0);
              return pieData.length >= 2 ? (
                <div className="rounded-lg border border-border bg-card p-5">
                  <p className="mb-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Breakdown
                  </p>
                  <PieChartWrapper data={pieData} height={220} />
                </div>
              ) : null;
            })()}
        </div>
      }
      chartSection={
        mounted ? (
          <AreaChartWrapper
            data={chartData}
            dataKeys={[
              { key: "balance", color: CHART_COLOR, name: "Projected balance" },
            ]}
          />
        ) : (
          <div className="h-[300px] w-full animate-pulse rounded-lg bg-muted" />
        )
      }
      explanationSection={
        <div className="space-y-6">
          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              The savings goal formula
            </h2>
            <p className="mb-3 text-muted-foreground">
              With monthly contributions and compound growth (monthly compounding):
            </p>
            <p className="mb-3 font-mono text-sm text-foreground">
              FV = PV × (1 + r/12)^(12t) + PMT × [((1 + r/12)^(12t) − 1) / (r/12)]
            </p>
            <p className="mb-3 text-muted-foreground">
              For quarterly or annual compounding, the rate and periods adjust. Most savings accounts compound monthly.
            </p>
            <p className="mb-3 text-muted-foreground">
              Where <strong className="text-foreground">FV</strong> = future
              value, <strong className="text-foreground">PV</strong> = current
              savings, <strong className="text-foreground">PMT</strong> = monthly
              contribution, <strong className="text-foreground">r</strong> =
              annual rate (decimal), and{" "}
              <strong className="text-foreground">t</strong> = years. To find
              the required monthly contribution, we solve for PMT given your
              target FV.
            </p>
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Example calculation
            </h2>
            <p className="mb-3 text-muted-foreground">
              Goal: $50,000 in 5 years. Current savings: $10,000. Expected return:
              6%.
            </p>
            <ExplanationTable
              headers={["Variable", "Value"]}
              rows={[
                ["Target (FV)", "$50,000"],
                ["Current (PV)", "$10,000"],
                ["Rate (r)", "0.06"],
                ["Years (t)", "5"],
                ["Required monthly (PMT)", "≈ $523"],
              ]}
            />
          </div>

          <div>
            <h2 className="mb-3 text-lg font-semibold text-foreground">
              Limitations
            </h2>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>Assumes a fixed rate of return; real investments fluctuate.</li>
              <li>Does not account for taxes, fees, or inflation.</li>
              <li>
                Use for projections and planning, not as a guarantee of future
                results.
              </li>
            </ul>
          </div>
        </div>
      }
    />
  );
}
